import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LockKeyhole } from "lucide-react";

const LOGO =
  "https://customer-assets-cm19k8pv.emergentagent.net/wingman/4be0c1cd-8cc3-450f-ab9d-6230338a9741/attachments/dbc363331499412bba1d8b502390e8c0_image.bin";

export default function AdminLogin() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(form.email.trim().toLowerCase(), form.password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7FB] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_30px_60px_-30px_rgba(11,37,69,0.4)] border border-[#EBEFF5] p-8">
        <div className="flex items-center gap-3">
          <img
            src={LOGO}
            alt="Richcube"
            className="h-[72px] w-auto rounded-md bg-white p-1.5 border border-[#EBEFF5] object-contain"
          />
          <div>
            <div className="font-serif text-lg text-[#0B2545]">{t.brand}</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A84C]">
              {t.brand_tagline}
            </div>
          </div>
        </div>

        <h1 className="mt-8 font-serif text-2xl text-[#0B2545] flex items-center gap-2">
          <LockKeyhole size={18} className="text-[#C9A84C]" />
          {t.admin.login_title}
        </h1>
        <p className="mt-1 text-sm text-[#1F2A44]">{t.admin.login_sub}</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#1F2A44]">
              {t.admin.email}
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="admin-login-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="h-11 border-[#EBEFF5] focus-visible:ring-[#C9A84C]"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#1F2A44]">
              {t.admin.password}
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-login-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              className="h-11 border-[#EBEFF5] focus-visible:ring-[#C9A84C]"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full h-11 bg-[#0B2545] text-[#C9A84C] hover:bg-[#133763] font-semibold tracking-wider uppercase text-xs"
          >
            {submitting ? "…" : t.admin.sign_in}
          </Button>
        </form>
      </div>
    </div>
  );
}
